import { useEffect, useMemo, useState } from 'react'
import type { FailureTag } from '../slices/failuresSlice'
import './TagFilterBar.css'

/** Max tag chips shown when collapsed (excluding "All"). */
const COLLAPSED_TAG_LIMIT = 6

function sortTagsByCount(tags: FailureTag[]): FailureTag[] {
  return [...tags].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Top `limit` tags by count; if `selected` is outside that set, show `limit - 1` top + selected. */
function getCollapsedTags(sorted: FailureTag[], selected: string | null, limit: number): FailureTag[] {
  if (sorted.length <= limit) return sorted
  const top = sorted.slice(0, limit)
  if (!selected || top.some((t) => t.name === selected)) return top
  const sel = sorted.find((t) => t.name === selected)
  if (!sel) return top
  return [...top.slice(0, limit - 1), sel]
}

interface TagFilterBarProps {
  tags: FailureTag[]
  selectedTag: string | null
  onSelectTag: (tag: string | null) => void
  onCreateTag: (name: string) => Promise<void>
}

function TagFilterBar({ tags, selectedTag, onSelectTag, onCreateTag }: TagFilterBarProps) {
  const [showCreateInput, setShowCreateInput] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [creating, setCreating] = useState(false)
  const [tagsExpanded, setTagsExpanded] = useState(false)

  /** Tags with at least one failure (upstream behavior); collapse UI applies to this list. */
  const tagsForChips = useMemo(
    () => sortTagsByCount(tags.filter((t) => t.count > 0)),
    [tags],
  )
  const collapsedTags = useMemo(
    () => getCollapsedTags(tagsForChips, selectedTag, COLLAPSED_TAG_LIMIT),
    [tagsForChips, selectedTag],
  )
  const showTagToggle = tagsForChips.length > COLLAPSED_TAG_LIMIT
  const visibleTags = tagsExpanded || !showTagToggle ? tagsForChips : collapsedTags
  const hiddenTagCount = tagsForChips.length - collapsedTags.length

  useEffect(() => {
    if (tagsForChips.length <= COLLAPSED_TAG_LIMIT) setTagsExpanded(false)
  }, [tagsForChips.length])

  async function handleCreateTag() {
    const trimmed = newTagName.trim()
    if (!trimmed) return
    setCreating(true)
    try {
      await onCreateTag(trimmed)
      setNewTagName('')
      setShowCreateInput(false)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div data-testid="tag-filter-bar" className="tag-filter-bar">
      <div className="tag-filter-chips">
        {tags.length > 0 && (
          <button
            type="button"
            data-testid="tag-filter-all"
            className={`tag-filter-chip ${selectedTag === null ? 'active' : ''}`}
            onClick={() => onSelectTag(null)}
          >
            All
          </button>
        )}
        {visibleTags.map((tag) => (
          <button
            type="button"
            key={tag.name}
            data-testid={`tag-filter-${tag.name}`}
            className={`tag-filter-chip ${selectedTag === tag.name ? 'active' : ''}`}
            onClick={() => onSelectTag(selectedTag === tag.name ? null : tag.name)}
          >
            {tag.name}
            <span className="tag-filter-count">{tag.count}</span>
            {selectedTag === tag.name && (
              <span className="tag-filter-dismiss" aria-label={`Clear ${tag.name} filter`}>&times;</span>
            )}
          </button>
        ))}
        {showTagToggle && (
          <button
            type="button"
            className="tag-filter-toggle"
            data-testid={tagsExpanded ? 'tag-filter-show-fewer' : 'tag-filter-show-all'}
            aria-expanded={tagsExpanded}
            onClick={() => setTagsExpanded((v) => !v)}
          >
            {tagsExpanded ? 'Show fewer' : `${hiddenTagCount} more…`}
          </button>
        )}
      </div>
      <div className="tag-filter-actions">
        {showCreateInput ? (
          <div data-testid="create-tag-form" className="create-tag-form">
            <input
              data-testid="create-tag-input"
              className="create-tag-input"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateTag()
                if (e.key === 'Escape') { setShowCreateInput(false); setNewTagName('') }
              }}
              placeholder="Tag name..."
              autoFocus
            />
            <button
              type="button"
              data-testid="create-tag-submit"
              className="create-tag-submit"
              onClick={handleCreateTag}
              disabled={creating || !newTagName.trim()}
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              className="create-tag-cancel"
              onClick={() => { setShowCreateInput(false); setNewTagName('') }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="create-tag-btn"
            className="create-tag-btn"
            onClick={() => setShowCreateInput(true)}
          >
            + New Tag
          </button>
        )}
      </div>
    </div>
  )
}

export default TagFilterBar
